import React, { useState, useCallback, useRef, useEffect, useContext } from "react";
import axios from 'axios';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../context/UserContext';

import Panel from '../ui/Panel';
import Btn from '../ui/Btn';
import Bar from '../ui/Bar';
import PlayerCard from '../ui/PlayerCard';
import './field.css';

/* ============================================================
   FIELD — the tactical board.

   Basketball only (SportID 2): five slots a side, drag & drop,
   live matchup analytics. Football (SportID 1, eleven a side)
   is not laid out on this screen and nothing here touches it.

   Every colour comes from theme/tokens.css and every rule from
   ./field.css — this file carries no inline style objects,
   including the ten slot coordinates, which live in field.css
   keyed on [data-zone] so placement is tuned in one place.
   ============================================================ */

/* ─── Sports ──────────────────────────────────────────────
   Two sports use this board and they are not the same shape.

   Basketball is a matchup: five starters on one half, five
   substitutes on the other, read against each other.

   Football is not. A formation occupies the WHOLE pitch — a back
   four sits deep, a front three sits high — and squeezing eleven
   players onto one half to leave room for a bench would draw a
   shape no coach has ever set up. So football gets one group of
   eleven across the full pitch and no opposing half.

   Everything below reads this object: the slots, the ratings, the
   position names, the artwork and the copy. Nothing downstream
   knows which sport it is drawing.

   The x/y for every slot lives in field.css under "SLOT
   COORDINATES", keyed on [data-zone].
   ────────────────────────────────────────────────────────── */

const BASKETBALL = {
  id: 2,
  boardSubtitle: "Basketball — five a side",
  zones: {
    "home-1": { label: "PG" },
    "home-2": { label: "SG" },
    "home-3": { label: "SF" },
    "home-4": { label: "PF" },
    "home-5": { label: "C"  },
    "away-1": { label: "PG" },
    "away-2": { label: "SG" },
    "away-3": { label: "SF" },
    "away-4": { label: "PF" },
    "away-5": { label: "C"  },
  },
  groups: [
    { key: "home", title: "Starters",    meta: "Left half of the court",  pick: "Left half" },
    { key: "away", title: "Substitutes", meta: "Right half of the court", pick: "Right half" },
  ],
  /* Keyed the way listscriteria/results/court delivers them: the stat
     name from the database with its spaces turned into underscores. */
  stats: [
    { key: "Ball_handling",  label: "Handling" },
    { key: "Percentage_2pt", label: "2PT %" },
    { key: "Percentage_3pt", label: "3PT %" },
    { key: "Percentage_FT",  label: "FT %" },
    { key: "Speed",          label: "Speed" },
  ],
  positionMap: {
    "POINT GUARD": "PG", "SHOOTING GUARD": "SG", "SMALL FORWARD": "SF",
    "POWER FORWARD": "PF", "CENTER": "C",
  },
};

const FOOTBALL = {
  id: 3,
  boardSubtitle: "Football — a starting eleven in 4-3-3",
  /* Read left to right, attacking right: keeper, back four, a midfield
     three, a front three. The labels are what a scout writes on a team
     sheet; positionMap maps them back to the position titles the
     database actually stores. */
  zones: {
    "fb-gk":  { label: "GK" },
    "fb-lb":  { label: "LB" },
    "fb-lcb": { label: "CB" },
    "fb-rcb": { label: "CB" },
    "fb-rb":  { label: "RB" },
    "fb-dm":  { label: "DM" },
    "fb-lcm": { label: "CM" },
    "fb-rcm": { label: "CM" },
    "fb-lw":  { label: "LW" },
    "fb-st":  { label: "ST" },
    "fb-rw":  { label: "RW" },
  },
  groups: [
    { key: "fb", title: "Starting XI", meta: "The whole pitch", pick: "Pitch" },
  ],
  stats: [
    { key: "Shot",    label: "Shot" },
    { key: "Dribble", label: "Dribble" },
    { key: "Head",    label: "Head" },
    { key: "Control", label: "Control" },
    { key: "Speed",   label: "Speed" },
  ],
  /* Nine position titles cover eleven slots, so several map to more than
     one: two centre backs, two centre midfielders, and a wing back who
     can take either flank. */
  positionMap: {
    "GOALKEEPER": ["GK"],
    "CENTER BACK": ["CB"],
    "WING BACK": ["LB", "RB"],
    "DEFENSIVE MIDFIELDER": ["DM"],
    "CENTRE MIDFIELDER": ["CM"],
    "SIDE MIDFIELDER": ["CM"],
    "ATTACKING MIDFIELDER": ["CM", "DM"],
    "WINGER": ["LW", "RW"],
    "CENTER FORWARD": ["ST"],
  },
};

/* Resolved inside the component, never here: this module is imported
   when the app boots, which is before anyone has signed in and before
   sessionStorage knows the sport. Reading it at import time would pin
   every account to basketball for the rest of the session. */
function sportConfig() {
  const id = parseInt(sessionStorage.getItem("SportID"), 10);
  return id === FOOTBALL.id ? FOOTBALL : BASKETBALL;
}

function zoneIdsOf(sport, groupKey) {
  return Object.keys(sport.zones).filter(z => z.slice(0, z.indexOf("-")) === groupKey);
}

function emptyCourt(sport) {
  return Object.fromEntries(Object.keys(sport.zones).map(k => [k, null]));
}

/* ─── Helpers ────────────────────────────────────────────── */
function positionMatches(sport, playerPos, expectedPos) {
  if (!playerPos || !expectedPos) return false;
  const p = String(playerPos).toUpperCase().trim();
  const e = String(expectedPos).toUpperCase().trim();
  if (p === e) return true;
  /* A position can be right for more than one slot — a wing back plays
     either flank, a winger either wing — so an entry is allowed to be a
     list as well as a single slot. */
  const allowed = sport.positionMap[p];
  if (!allowed) return false;
  return Array.isArray(allowed) ? allowed.indexOf(e) > -1 : allowed === e;
}

function calcTeamStats(sport, playerIds, allPlayers) {
  const players = playerIds.map(id => allPlayers.find(p => p.id == id)).filter(Boolean);
  const stats = sport.stats.map(({ key, label }) => {
    const avg = players.length
      ? (players.reduce((s, p) => s + parseFloat(p[key] || 0), 0) / players.length).toFixed(1)
      : 0;
    return { key, label, avg: parseFloat(avg) };
  });
  return { count: players.length, stats };
}

function generateInsight(sport, groups) {
  const football = sport.id === FOOTBALL.id;
  const surface = football ? "pitch" : "court";
  const placed = groups.reduce((n, g) => n + g.stats.count, 0);

  if (!placed) return `Place players on the ${surface} to receive strategy analysis...`;

  const first = groups[0];
  if (first.stats.count < first.size) {
    const left = first.size - first.stats.count;
    return `${first.group.title} has ${first.stats.count} of ${first.size}. ${left} more to go before a read means anything.`;
  }

  /* Do not invent a read out of nothing. Every rating comes from the
     player's own profile, and a squad where none of them have been
     filled in averages zero across the board — saying "great ball
     handling" about that would be a lie the scout could act on. */
  const anyRating = groups.some(g => g.stats.stats.some(x => x.avg > 0));
  if (!anyRating) {
    return "Nobody on the board carries a skill rating yet, so there is nothing to compare. Ratings come from the players' own profiles.";
  }

  const avg = (g, key) => g.stats.stats.find(x => x.key === key)?.avg ?? 0;

  if (football) {
    const xi = groups[0];
    const pace = avg(xi, "Speed");
    const shot = avg(xi, "Shot");
    const control = avg(xi, "Control");
    const head = avg(xi, "Head");
    const dribble = avg(xi, "Dribble");

    if (shot > 75 && shot >= pace) return "This eleven finishes. Work the ball into shooting range rather than running at people.";
    if (pace > 75 && pace > control + 10) return "Quick, but loose in possession. This side is better breaking at speed than holding the ball.";
    if (control > 75 && dribble > 70) return "Comfortable on the ball through the middle. A patient build-up suits this eleven.";
    if (head > 75) return "Strong in the air. Set pieces and crosses are where this side is most dangerous.";
    return "A balanced eleven with no standout strength. The margin will come from the wide areas.";
  }

  const home = groups[0];
  const away = groups[1];
  if (away.stats.count < away.size) return "Place all players on both sides to get a full matchup analysis.";

  const hs = (avg(home, "Percentage_2pt") + avg(home, "Percentage_3pt")) / 2;
  if (hs > 45 && hs > avg(away, "Speed")) return "Your lineup has amazing shooters! Spread the floor and focus on kick-out 3-pointers.";
  if (avg(away, "Speed") > avg(home, "Ball_handling") + 10) return "The opponent is much faster. Be careful of turnovers and transition fast-breaks!";
  if (avg(home, "Ball_handling") > 85) return "Great ball handling in your team. Focus on Pick & Roll to dismantle their defense.";
  return "Very balanced matchup. Victory will come down to rebounds and free throw execution.";
}

function initialsOf(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '—';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function lastNameOf(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  return parts.length ? parts[parts.length - 1] : '—';
}

/* profile endpoints return either the record or a one-row array */
function firstRecord(payload) {
  if (Array.isArray(payload)) return payload[0] || {};
  return payload || {};
}

/* ─── Icons ──────────────────────────────────────────────── */
const IcoSearch = () => (
  <svg className="fc-search-ico" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="11" cy="11" r="7" /><path d="M20.2 20.2l-3.7-3.7" />
  </svg>
);
const IcoReset = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M20 11a8 8 0 10-2.3 5.7" /><path d="M20 5v6h-6" />
  </svg>
);
const IcoDown = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 4v13" /><path d="M6.5 11.5L12 17l5.5-5.5" /><path d="M5 20h14" />
  </svg>
);
const IcoOut = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M6 6l12 12" /><path d="M18 6L6 18" />
  </svg>
);

/* ─── Avatar (image with an initials fallback) ─────────────
   draggable defaults to FALSE: images are natively draggable and
   would otherwise hijack the drag from the roster row that owns it.
   The court slots opt in explicitly so a placed player can be moved. */
function Avatar({ src, name, draggable, onDragStart }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [src]);
  const canDrag = draggable === true;

  if (!src || broken) {
    return (
      <span
        className="fc-av fc-av-fallback"
        draggable={canDrag}
        onDragStart={canDrag ? onDragStart : undefined}
      >
        {initialsOf(name)}
      </span>
    );
  }
  return (
    <img
      className="fc-av"
      src={src}
      alt={name || 'Player'}
      draggable={canDrag}
      onDragStart={canDrag ? onDragStart : undefined}
      onError={() => setBroken(true)}
    />
  );
}

/* ─── Toast ──────────────────────────────────────────────── */
function Toast({ message, onDone }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t1 = setTimeout(() => setVisible(true), 10);
    const t2 = setTimeout(() => setVisible(false), 3500);
    const t3 = setTimeout(onDone, 3900);
    return () => [t1, t2, t3].forEach(clearTimeout);
  }, [onDone]);

  return (
    <div className="fc-toast" data-open={visible ? "1" : "0"} role="status" aria-live="polite">
      <span className="fc-toast-mark" />
      {message}
    </div>
  );
}

/* ─── Player Modal ───────────────────────────────────────── */
function PlayerModal({ player, onClose, onPlace, isOnCourt, onRemove }) {
  const [show, setShow] = useState(false);
  const [profile, setProfile] = useState([]);
  const [stats, setStats] = useState([]);
  const [failed, setFailed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { const t = setTimeout(() => setShow(true), 10); return () => clearTimeout(t); }, []);

  useEffect(() => {
    if (show) {
      loadProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const loadProfile = () => {
    axios.get(process.env.REACT_APP_API_SERVER + 'manager/dashboard/userprofile/' + player.UUID, {
      headers: { 'Authorization': 'Bearer ' + sessionStorage.getItem("mngapiToken") }
    })
      .then(res => {
        setProfile(res.data.results);
        loadStats();
      }).catch(err => {
        setFailed(true);
      });
  };

  const loadStats = () => {
    axios.get(process.env.REACT_APP_API_SERVER + 'manager/dashboard/userstats/' + player.UUID, {
      headers: { 'Authorization': 'Bearer ' + sessionStorage.getItem("mngapiToken") }
    })
      .then(res => {
        setStats(res.data.results);
      }).catch(err => {
        setFailed(true);
      });
  };

  const close = () => { setShow(false); setTimeout(onClose, 300); };

  const pf = firstRecord(profile);
  const age = pf.DateOfBirthYMD ? moment().diff(moment(pf.DateOfBirthYMD), 'years') : null;
  const foot = pf.LeftRight == 1 ? 'Right' : pf.LeftRight == 2 ? 'Left' : 'Both';

  const meta = [
    ['Age', Number.isFinite(age) && age > 0 ? age : null],
    ['Tag', pf.Nickname],
    ['Height', pf.Height ? pf.Height + ' cm' : null],
    ['Weight', pf.Weight ? pf.Weight + ' kg' : null],
    ['Country', pf.Country],
    ['Preferred', pf.LeftRight ? foot : null],
  ];

  const rows = Array.isArray(stats) ? stats : [];

  return (
    <div className="fc-overlay" data-open={show ? "1" : "0"} onClick={close} role="dialog" aria-modal="true">
      <div className="fc-modal" onClick={e => e.stopPropagation()}>
        <button className="fc-modal-x" onClick={close} aria-label="Close">✕</button>

        <div className="fc-modal-grid">
          <span className="fc-modal-photo">
            <Avatar src={player.avatar_url} name={player.fullname} />
          </span>

          <div>
            <button className="fc-modal-name" onClick={() => navigate('/user/' + player.UUID)}>
              {player.fullname}
            </button>

            <div className="fc-tagline">
              <span className="fc-tag">{player.position || 'No position'}</span>
              <span className="fc-count"><b className="num">{player.StatsOverall}</b> Overall</span>
              {isOnCourt ? (
                <Btn onClick={() => { onRemove(player.id); onClose(); }}>
                  <IcoOut /> Remove from court
                </Btn>
              ) : (
                <Btn variant="accent" onClick={() => { onClose(); onPlace(player); }}>
                  <IcoDown /> Place on court
                </Btn>
              )}
            </div>

            <div className="fc-meta">
              {meta.map(([label, value]) => (
                <div className="fc-meta-item" key={label}>
                  <span className="mono fc-meta-label">{label}</span>
                  <span className="fc-meta-value num">{value || '—'}</span>
                </div>
              ))}
            </div>

            <span className="mono fc-section">Skill ratings</span>
            {rows.length > 0 ? (
              <div className="bars">
                {rows.map((stat, index) => (
                  <Bar
                    key={stat.StatName || index}
                    label={stat.StatName}
                    value={stat.Value}
                    display={stat.Value}
                    max={100}
                    delay={index * 0.05}
                  />
                ))}
              </div>
            ) : (
              <div className="empty">{failed ? 'Ratings unavailable right now.' : 'Loading ratings…'}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Team Pick Modal ────────────────────────────────────── */
function TeamPickModal({ player, groups, onPick, onClose }) {
  const [show, setShow] = useState(false);
  useEffect(() => { const t = setTimeout(() => setShow(true), 10); return () => clearTimeout(t); }, []);
  const close = () => { setShow(false); setTimeout(onClose, 300); };
  const pick = (prefix) => { setShow(false); setTimeout(() => { onPick(player.id, prefix); onClose(); }, 300); };

  return (
    <div className="fc-overlay" data-open={show ? "1" : "0"} onClick={close} role="dialog" aria-modal="true">
      <div className="fc-modal fc-modal-sm" onClick={e => e.stopPropagation()}>
        <button className="fc-modal-x" onClick={close} aria-label="Close">✕</button>

        <div className="fc-pick">
          <span className="fc-pick-photo">
            <Avatar src={player.avatar_url} name={player.fullname} />
          </span>
          <div className="fc-pick-name">{player.fullname}</div>
          <div className="fc-pick-sub">Which side does this player join?</div>

          <div className="fc-pick-acts">
            {groups.map(g => (
              <button
                key={g.key}
                className={'fc-pick-btn ' + (g.key === 'away' ? 'fc-pick-away' : 'fc-pick-home')}
                onClick={() => pick(g.key)}
              >
                <span className={'fc-pip ' + (g.key === 'away' ? 'fc-pip-away' : 'fc-pip-home')} />
                {g.title}
                <span className="mono">{g.pick}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Court markings ─────────────────────────────────────────
   Drawn to FIBA proportions in centimetres (2800 x 1500) so the
   art scales with the board and every stroke is a token colour.
   ────────────────────────────────────────────────────────── */
function CourtArt() {
  return (
    <svg className="fc-art" viewBox="0 0 2800 1500" aria-hidden="true" focusable="false">
      {/* boundary + halfway */}
      <rect className="fc-line fc-line-edge" x="8" y="8" width="2784" height="1484" rx="10" />
      <path className="fc-line fc-line-edge" d="M1400 8 L1400 1492" />
      <circle className="fc-line" cx="1400" cy="750" r="180" />
      <circle className="fc-centre" cx="1400" cy="750" r="58" />

      {/* left half — Starters */}
      <rect className="fc-paint" x="8" y="505" width="572" height="490" />
      <rect className="fc-line" x="8" y="505" width="572" height="490" />
      <circle className="fc-line" cx="580" cy="750" r="180" />
      <path className="fc-line" d="M8 90 L299 90 A675 675 0 0 1 299 1410 L8 1410" />
      <path className="fc-line" d="M120 625 L157.5 625 A125 125 0 0 1 157.5 875 L120 875" />
      <path className="fc-hoop" d="M120 655 L120 845" />
      <circle className="fc-hoop" cx="180" cy="750" r="24" />

      {/* right half — Substitutes */}
      <rect className="fc-paint" x="2220" y="505" width="572" height="490" />
      <rect className="fc-line" x="2220" y="505" width="572" height="490" />
      <circle className="fc-line" cx="2220" cy="750" r="180" />
      <path className="fc-line" d="M2792 90 L2501 90 A675 675 0 0 0 2501 1410 L2792 1410" />
      <path className="fc-line" d="M2680 625 L2642.5 625 A125 125 0 0 0 2642.5 875 L2680 875" />
      <path className="fc-hoop" d="M2680 655 L2680 845" />
      <circle className="fc-hoop" cx="2620" cy="750" r="24" />
    </svg>
  );
}

/* ─── Drop Zone ──────────────────────────────────────────────
   The drop contract is untouched: dragOver/dragEnter preventDefault,
   drop hands (event, zoneId) straight back to the page. The zone now
   also reports which slot the pointer is aiming at (onAim) so the
   roster can dim the players that do not play that position, and an
   empty slot can be *selected* by click for the same hint without a
   drag. */
/* ─── Pitch markings ─────────────────────────────────────────
   A full pitch at 105 x 68 metres, scaled by 20 so the viewBox
   matches the court's 2800-unit width and the two share one stage.
   Same stroke tokens, same weights — only the shape differs.
   ────────────────────────────────────────────────────────── */
function PitchArt() {
  return (
    <svg className="fc-art" viewBox="0 0 2800 1500" aria-hidden="true" focusable="false">
      {/* touchlines + halfway */}
      <rect className="fc-line fc-line-edge" x="8" y="8" width="2784" height="1484" rx="10" />
      <path className="fc-line fc-line-edge" d="M1400 8 L1400 1492" />
      <circle className="fc-line" cx="1400" cy="750" r="244" />
      <circle className="fc-centre" cx="1400" cy="750" r="26" />

      {/* left penalty area, six-yard box, spot and D */}
      <rect className="fc-paint" x="8" y="300" width="440" height="900" />
      <rect className="fc-line" x="8" y="300" width="440" height="900" />
      <rect className="fc-line" x="8" y="567" width="147" height="366" />
      <circle className="fc-centre" cx="320" cy="750" r="16" />
      <path className="fc-line" d="M448 592 A244 244 0 0 1 448 908" />
      <path className="fc-hoop" d="M8 640 L8 860" />

      {/* right penalty area, mirrored */}
      <rect className="fc-paint" x="2352" y="300" width="440" height="900" />
      <rect className="fc-line" x="2352" y="300" width="440" height="900" />
      <rect className="fc-line" x="2645" y="567" width="147" height="366" />
      <circle className="fc-centre" cx="2480" cy="750" r="16" />
      <path className="fc-line" d="M2352 592 A244 244 0 0 0 2352 908" />
      <path className="fc-hoop" d="M2792 640 L2792 860" />

      {/* corner arcs */}
      <path className="fc-line" d="M8 60 A52 52 0 0 0 60 8" />
      <path className="fc-line" d="M2740 8 A52 52 0 0 0 2792 60" />
      <path className="fc-line" d="M8 1440 A52 52 0 0 1 60 1492" />
      <path className="fc-line" d="M2740 1492 A52 52 0 0 1 2792 1440" />
    </svg>
  );
}

function DropZone({
  zoneId, playerId, allPlayers, onDragStart, onDrop, onClickFilled,
  label, removePlayer, onAim, onSelect, isAimed,
}) {
  const [isDragOver, setIsDragOver] = useState(false);
  const player = playerId ? allPlayers.find(p => p.id == playerId) : null;
  const isHome = zoneId.startsWith("home");

  const classes = [
    'fc-slot',
    isHome ? 'fc-slot-home' : 'fc-slot-away',
    player ? 'is-filled' : 'is-empty',
    isDragOver ? 'is-over' : '',
    isAimed ? 'is-aimed' : '',
  ].filter(Boolean).join(' ');

  /* dragleave also fires when the pointer crosses onto a child of the
     slot, so dragover re-asserts the aim on every tick — setting the
     same zone again is a no-op for React. */
  return (
    <div
      className={classes}
      data-zone={zoneId}
      onDragOver={e => { e.preventDefault(); setIsDragOver(true); onAim(zoneId); }}
      onDragEnter={e => { e.preventDefault(); setIsDragOver(true); onAim(zoneId); }}
      onDragLeave={() => { setIsDragOver(false); onAim(null); }}
      onDrop={e => { setIsDragOver(false); onAim(null); onDrop(e, zoneId); }}
      onClick={player ? () => onClickFilled(player.id) : () => onSelect(zoneId)}
      title={player ? player.fullname : label}
    >
      {player ? (
        <>
          <span className="fc-slot-av">
            <Avatar
              src={player.avatar_url}
              name={player.fullname}
              draggable
              onDragStart={e => onDragStart(e, String(player.id), zoneId)}
            />
          </span>

          <span className="mono fc-slot-pos">{label}</span>

          <span className="fc-plate">
            <span className="fc-plate-name">{lastNameOf(player.fullname)}</span>
          </span>

          <button
            className="fc-slot-x"
            aria-label={'Remove ' + player.fullname}
            onClick={e => { e.stopPropagation(); removePlayer(player.id); }}
          >✕</button>
        </>
      ) : (
        <span className="fc-slot-label">{label}</span>
      )}
    </div>
  );
}

/* ─── Roster row ─────────────────────────────────────────── */
function RosterRow({ player, onDragStart, onClick, onDoubleClick, dimmed }) {
  const clickTimer = useRef(null);

  useEffect(() => () => clearTimeout(clickTimer.current), []);

  const handleClick = () => {
    clearTimeout(clickTimer.current);
    clickTimer.current = setTimeout(() => onClick(player.id), 250);
  };
  const handleDoubleClick = () => {
    clearTimeout(clickTimer.current);
    onDoubleClick(player.id);
  };

  return (
    <li
      className="fc-row"
      data-dim={dimmed ? '1' : '0'}
      draggable
      onDragStart={e => onDragStart(e, String(player.id), null)}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
    >
      <PlayerCard
        uuid={player.UUID}
        name={player.fullname}
        position={player.position}
        points={player.StatsOverall}
      >
        <span className="fc-av-hold">
          <Avatar src={player.avatar_url} name={player.fullname} />
        </span>
      </PlayerCard>

      <span className="fc-row-who">
        <span className="fc-row-name">{player.fullname}</span>
        <span className="fc-row-pos">{player.position || 'No position'}</span>
      </span>

      <span className="fc-row-score">
        <b className="num">{player.StatsOverall}</b>
        <span className="mono">OVR</span>
      </span>
    </li>
  );
}

/* ─── Side analytics ─────────────────────────────────────── */
function SideStats({ group, size, side, teamStats }) {
  return (
    <Panel
      className="fc-side"
      title={group.title}
      subtitle="Team average"
      actions={<span className="fc-count"><b className="num">{teamStats.count}</b>/{size}</span>}
    >
      {teamStats.count === 0 ? (
        <div className="fc-empty-side">No players here yet.</div>
      ) : (
        <div className="bars">
          {teamStats.stats.map(({ key, label, avg }, i) => (
            <Bar key={key} label={label} value={avg} display={avg.toFixed(1)} max={100} delay={i * 0.05} />
          ))}
        </div>
      )}
      <div className="fc-side-head">
        <span className={'fc-pip ' + (side === 'away' ? 'fc-pip-away' : 'fc-pip-home')} />
        <span className="mono fc-meta-label">{group.meta}</span>
      </div>
    </Panel>
  );
}

/* ─── Root Component ─────────────────────────────────────── */
export default function ProScoutElite() {
  /* Lazy initialiser, so sessionStorage is read on the first render of
     a signed-in session rather than when this module was imported. */
  const [SPORT] = useState(sportConfig);

  const [playersData, setPlayersData] = useState([]);
  const [courtState, setCourtState] = useState(() => emptyCourt(SPORT));
  const [currentTab] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [modalPlayer, setModalPlayer] = useState(null);
  const [teamPickPlayer, setTeamPickPlayer] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  /* the slot the scout picked by clicking it (sticky) */
  const [selectedZone, setSelectedZone] = useState(null);
  /* the slot a drag is currently hovering (transient, wins over the above) */
  const [hoverZone, setHoverZone] = useState(null);
  const dragData = useRef(null);

  const { selectedList } = useContext(UserContext);
  const { setFieldTeam } = useContext(UserContext);

  useEffect(() => {
    const savedCourt = localStorage.getItem("courtState");
    if (savedCourt) {
      try {
        /* A board saved under the other sport has slot names this one
           has never heard of, so only known slots are taken back. */
        const saved = JSON.parse(savedCourt);
        const blank = emptyCourt(SPORT);
        Object.keys(saved).forEach(k => { if (k in blank) blank[k] = saved[k]; });
        setCourtState(blank);
      } catch (e) {
        setCourtState(emptyCourt(SPORT));
      }
    }
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem("courtState", JSON.stringify(courtState));
  }, [courtState]);

  useEffect(() => {
    localStorage.setItem("selectedList", selectedList);
  }, [selectedList]);

  /* Escape drops the aim, so the roster never stays dimmed by accident. */
  useEffect(() => {
    if (!selectedZone) return undefined;
    const onKey = e => { if (e.key === 'Escape') setSelectedZone(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedZone]);

  const loadList = () => {
    setIsLoading(true);
    setLoadError(false);
    const SportID = sessionStorage.getItem('SportID');
    const CountryIDS = sessionStorage.getItem('appCountries') ? JSON.parse(sessionStorage.getItem('appCountries')).map(c => (c.value)) : [];
    axios.post(process.env.REACT_APP_API_SERVER + 'manager/listscriteria/results/court', { SportID, CountryIDS }, {
      headers: { 'Authorization': 'Bearer ' + sessionStorage.getItem("mngapiToken") }
    })
      .then(res => {
        if (res.data.err == 0) {
          const listResults = res.data.results.sort((a, b) => b.StatsOverall - a.StatsOverall)
          const resForCourt = listResults.map(player => ({
            UUID: player.UUID,
            id: player.UUID,
            fullname: `${player.FirstName.trim()} ${player.LastName.trim()}`,
            position: player.Position,
            team_id: 1,
            avatar_url: `${process.env.REACT_APP_API_SERVER}files/${player.UUID}/123`,
            StatsOverall: player.StatsOverall ? player.StatsOverall : 0,
            /* Whatever ratings this sport defines, read off whatever the
               API sent. The five basketball keys used to be written out
               here, which meant a football player arrived with five
               basketball ratings of zero and none of his own. */
            ...Object.fromEntries(
              SPORT.stats.map(({ key }) => [key, Number((player.stats || {})[key]) || 0])
            ),
          })).sort((a, b) => b.StatsOverall - a.StatsOverall)
          setPlayersData(resForCourt);
        } else {
          setLoadError(true);
        }
        setIsLoading(false);
      }).catch(err => {
        setIsLoading(false);
        setLoadError(true);
      });
  };

  const removePlayer = useCallback((playerID) => {
    setCourtState(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(zoneID => {
        if (next[zoneID] == playerID) next[zoneID] = null;
      });
      return next;
    });
    setFieldTeam(prev => prev.filter(p => p.id != playerID));
  }, [setFieldTeam]);

  const doReset = () => {
    setCourtState(emptyCourt(SPORT));
    setFieldTeam([]);
    setConfirmReset(false);
    setSelectedZone(null);
    setHoverZone(null);
  };

  /* clicking an empty slot arms it; clicking it again disarms it */
  const selectZone = useCallback(zoneId => {
    setSelectedZone(prev => (prev === zoneId ? null : zoneId));
  }, []);

  const aimAt = useCallback(zoneId => setHoverZone(zoneId), []);

  const showToast = useCallback(msg => setToast(msg), []);

  const assignedIds = new Set(Object.values(courtState).filter(Boolean).map(String));
  const needle = searchText.toLowerCase();
  const visiblePlayers = playersData.filter(p =>
    p.team_id === currentTab &&
    (String(p.fullname).toLowerCase().includes(needle) || String(p.position || '').toLowerCase().includes(needle)) &&
    !assignedIds.has(String(p.id))
  );

  /* One entry per group the sport defines — two for basketball, one for
     football — so the panels, the counts and the insight all come from
     the same place instead of from two hard-coded halves. */
  const groupViews = SPORT.groups.map(group => {
    const zoneIds = zoneIdsOf(SPORT, group.key);
    return {
      group,
      zoneIds,
      size: zoneIds.length,
      stats: calcTeamStats(SPORT, zoneIds.map(k => courtState[k]).filter(Boolean), playersData),
    };
  });
  const insight = generateInsight(SPORT, groupViews);
  const placed = groupViews.reduce((n, g) => n + g.stats.count, 0);
  const totalSlots = Object.keys(SPORT.zones).length;

  /* THE PLACEMENT HINT
     A live drag over a slot beats a sticky click-selection, and a
     selection that has since been filled counts for nothing. aimPos is
     the position that slot is asking for; every roster row whose own
     position is not that one gets dimmed. It is a hint only — nothing
     is disabled, nothing is filtered out of the list. */
  const aimZone = hoverZone || (selectedZone && !courtState[selectedZone] ? selectedZone : null);
  const aimPos = aimZone ? (SPORT.zones[aimZone] || {}).label : null;

  const handleDragStart = useCallback((e, playerId, sourceZone) => {
    dragData.current = { playerId, sourceZone };
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleDrop = useCallback((e, zoneId) => {
    e.preventDefault();
    setHoverZone(null);
    setSelectedZone(null);
    if (!dragData.current) return;
    const { playerId, sourceZone } = dragData.current;
    dragData.current = null;

    const player = playersData.find(p => p.id == playerId);
    if (!player) return;

    const expectedPos = SPORT.zones[zoneId]?.label;
    if (!positionMatches(SPORT, player.position, expectedPos)) {
      showToast(`This slot needs a ${expectedPos} — ${player.fullname} is a ${player.position || 'player with no listed position'}.`);
      return;
    }

    const playerObject = {
      id: player.UUID || player.id,
      zoneID: zoneId,
      userUUID: sessionStorage.getItem('userToken'),
    };
    setFieldTeam(prev => [...prev.filter(p => p.zoneID !== zoneId), playerObject]);

    setCourtState(prev => {
      const next = { ...prev };
      if (sourceZone && sourceZone !== zoneId) next[sourceZone] = null;
      next[zoneId] = String(playerId);
      return next;
    });
  }, [SPORT, playersData, showToast, setFieldTeam]);

  const handleAutoPlace = useCallback((playerId, prefix) => {
    const player = playersData.find(p => p.id == playerId);
    if (!player) return;
    const candidates = zoneIdsOf(SPORT, prefix);
    for (let i = 0; i < candidates.length; i++) {
      const zId = candidates[i];
      if (!courtState[zId] && positionMatches(SPORT, player.position, SPORT.zones[zId]?.label)) {
        setCourtState(prev => ({ ...prev, [zId]: String(player.id) }));
        return;
      }
    }
    showToast(`No free ${player.position || 'matching'} slot available on that side.`);
  }, [SPORT, playersData, courtState, showToast]);

  /* Asking "which side?" only makes sense when there is more than one.
     A football XI has a single group, so the question is skipped and the
     player goes straight to the first slot that fits.

     handleAutoPlace is a dependency, not a closed-over convenience: it
     is rebuilt whenever the roster or the board changes, and holding the
     first version of it meant looking the player up in a roster that had
     not loaded yet — which failed silently, placing nobody. */
  const askPlacement = useCallback((player) => {
    if (!player) return;
    if (SPORT.groups.length === 1) {
      handleAutoPlace(player.id, SPORT.groups[0].key);
      return;
    }
    setTeamPickPlayer(player);
  }, [SPORT, handleAutoPlace]);

  return (
    <div className="fc-page">

      {isLoading && (
        <div className="fc-notice">
          <span className="fc-notice-mark" />
          <span className="mono">Loading roster</span>
          <span>Fetching the players available for this board…</span>
        </div>
      )}

      {loadError && !isLoading && (
        <div className="fc-notice fc-notice-crit" role="alert">
          <span className="fc-notice-mark" />
          <span className="mono">Roster failed</span>
          <span>The roster could not be loaded. The board still works with whatever is already placed.</span>
          <span className="fc-notice-acts"><Btn onClick={loadList}>Try again</Btn></span>
        </div>
      )}

      <div className="fc-layout">

        {/* ── Roster — the primary column ────────────── */}
        <div className="fc-col fc-col-roster">
          <Panel
            className="fc-roster-panel"
            title="Roster"
            subtitle={`${visiblePlayers.length} available`}
          >
            <div className="fc-search">
              <IcoSearch />
              <input
                className="fc-search-input"
                type="text"
                placeholder="Search name or position…"
                value={searchText}
                aria-label="Search the roster"
                onChange={e => setSearchText(e.target.value)}
              />
              {searchText && (
                <button className="fc-search-clear" aria-label="Clear search" onClick={() => setSearchText('')}>✕</button>
              )}
            </div>

            {aimPos && (
              <div className="fc-aim" role="status" aria-live="polite">
                <span className="mono fc-aim-pos">{aimPos}</span>
                <span className="fc-aim-text">
                  This slot takes a <b>{aimPos}</b>. Everyone else is faded back, and dropping
                  them here will be refused.
                </span>
              </div>
            )}

            <ul className="fc-roster scroll-body h-court">
              {visiblePlayers.map(p => (
                <RosterRow
                  key={p.id}
                  player={p}
                  dimmed={!!aimPos && !positionMatches(SPORT, p.position, aimPos)}
                  onDragStart={handleDragStart}
                  onClick={id => setModalPlayer(playersData.find(pl => pl.id == id))}
                  onDoubleClick={id => askPlacement(playersData.find(pl => pl.id == id))}
                />
              ))}
              {!visiblePlayers.length && (
                <li className="empty">
                  {isLoading ? 'Loading…' : searchText ? 'No players match that search.' : 'Every available player is on the court.'}
                </li>
              )}
            </ul>

            <div className="fc-hint">
              <b>Drag</b> a player onto a slot, <b>double-click</b> to auto-place, or <b>hover the photo</b> for their scouting card.
              Click an empty slot to see who fits it — click it again, or press <b>Esc</b>, to clear.
            </div>
          </Panel>
        </div>

        {/* ── Court ──────────────────────────────────── */}
        <div className="fc-col">
          <Panel
            className="fc-board"
            title="Tactical Board"
            subtitle={SPORT.boardSubtitle}
            actions={
              <span className="fc-tools">
                <span className="fc-count"><b className="num">{placed}</b>/{totalSlots} placed</span>
                <Btn onClick={() => setConfirmReset(true)} disabled={placed === 0}>
                  <IcoReset /> Reset
                </Btn>
              </span>
            }
          >
            {confirmReset && (
              <div className="fc-confirm" role="alertdialog" aria-label="Confirm reset">
                <span>Clear all {totalSlots} slots? This cannot be undone.</span>
                <span className="fc-confirm-acts">
                  <Btn onClick={() => setConfirmReset(false)}>Cancel</Btn>
                  <Btn variant="accent" onClick={doReset}>Clear board</Btn>
                </span>
              </div>
            )}

            <div className="fc-stage" data-sport={SPORT.id === FOOTBALL.id ? 'football' : 'basketball'}>
              {/* The tinted halves and the two captions belong to a
                  matchup. Football has one team across the whole pitch,
                  so there is nothing to divide and nothing to label. */}
              {SPORT.groups.length > 1 && (
                <>
                  <span className="fc-half fc-half-home" />
                  <span className="fc-half fc-half-away" />
                </>
              )}

              {SPORT.id === FOOTBALL.id ? <PitchArt /> : <CourtArt />}

              {SPORT.groups.length > 1 && (
                <>
                  <span className="mono fc-caption fc-caption-home">
                    <span className="fc-pip fc-pip-home" /> {SPORT.groups[0].title}
                  </span>
                  <span className="mono fc-caption fc-caption-away">
                    {SPORT.groups[1].title} <span className="fc-pip fc-pip-away" />
                  </span>
                </>
              )}

              <div className="fc-slots">
                {Object.entries(SPORT.zones).map(([zoneId, zone]) => (
                  <DropZone
                    key={zoneId}
                    zoneId={zoneId}
                    playerId={courtState[zoneId]}
                    allPlayers={playersData}
                    onDragStart={handleDragStart}
                    onDrop={handleDrop}
                    onClickFilled={id => setModalPlayer(playersData.find(p => p.id == id))}
                    label={zone.label}
                    removePlayer={removePlayer}
                    onAim={aimAt}
                    onSelect={selectZone}
                    isAimed={aimZone === zoneId}
                  />
                ))}
              </div>
            </div>
          </Panel>
        </div>

        {/* ── Analytics ──────────────────────────────── */}
        <div className="fc-col">
          {groupViews.map((view, i) => (
            <React.Fragment key={view.group.key}>
              {i > 0 && <div className="mono fc-vs">VS</div>}
              <SideStats
                group={view.group}
                size={view.size}
                side={view.group.key === 'away' ? 'away' : 'home'}
                teamStats={view.stats}
              />
            </React.Fragment>
          ))}

          <Panel title="Scout Insight" subtitle={SPORT.groups.length > 1 ? 'Live read of the matchup' : 'Live read of the eleven'}>
            <div className="callout">{insight}</div>
          </Panel>
        </div>
      </div>

      {modalPlayer && (
        <PlayerModal
          player={modalPlayer}
          onClose={() => setModalPlayer(null)}
          onPlace={p => { setModalPlayer(null); askPlacement(p); }}
          isOnCourt={assignedIds.has(String(modalPlayer.id))}
          onRemove={removePlayer}
        />
      )}
      {teamPickPlayer && (
        <TeamPickModal
          player={teamPickPlayer}
          groups={SPORT.groups}
          onPick={handleAutoPlace}
          onClose={() => setTeamPickPlayer(null)}
        />
      )}
      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
    </div>
  );
}
